/**
 * Glowwa growth timeline — product pills + step carousel per panel.
 * Figma: growth journey mobile + desktop frames.
 */

(function () {
  'use strict';

  const ROOT = '[data-glowwa-growth-timeline]';
  const STEP_TRANSITION_MS = 400;
  const MOBILE_MQ = window.matchMedia('(max-width: 989px)');
  const MOBILE_RAIL_VISIBLE_STEPS = 2;
  const SCROLL_FADE_THRESHOLD = 4;
  const prefersReducedMotion = window.matchMedia(
    '(prefers-reduced-motion: reduce)',
  ).matches;

  /**
   * Number of rail steps visible across the mobile viewport. Lower values
   * widen the gap between dots. Read from CSS so spacing stays themeable.
   *
   * @param {HTMLElement} panel
   * @returns {number}
   */
  function getMobileVisibleSteps(panel) {
    const value = parseFloat(
      getComputedStyle(panel).getPropertyValue(
        '--glowwa-gt-mobile-rail-visible-steps',
      ),
    );

    if (!Number.isFinite(value) || value <= 0) {
      return MOBILE_RAIL_VISIBLE_STEPS;
    }

    return value;
  }

  /**
   * @param {HTMLElement} panel
   * @returns {number}
   */
  function getStepCount(panel) {
    return Number(panel.dataset.glowwaGtStepCount || '0');
  }

  /**
   * @param {HTMLElement} panel
   * @returns {number}
   */
  function getActiveStepIndex(panel) {
    const active = panel.querySelector('[data-glowwa-gt-step].is-active');
    if (!active) {
      return 0;
    }
    return Number(active.dataset.glowwaGtStepIndex || '0');
  }

  /**
   * @param {HTMLElement} stepsRoot
   */
  function syncStepsHeight(stepsRoot) {
    const steps = stepsRoot.querySelectorAll('[data-glowwa-gt-step]');
    let maxHeight = 0;

    steps.forEach((step) => {
      const height = step.scrollHeight;
      if (height > maxHeight) {
        maxHeight = height;
      }
    });

    if (maxHeight > 0) {
      stepsRoot.style.minHeight = `${maxHeight}px`;
    }
  }

  /**
   * @param {HTMLElement} scrollEl
   * @param {HTMLElement} panel
   */
  function updateRailScrollFade(scrollEl, panel) {
    if (!(scrollEl instanceof HTMLElement)) {
      return;
    }

    if (!MOBILE_MQ.matches) {
      scrollEl.removeAttribute('data-scroll-fade');
      return;
    }

    const count = panel ? getStepCount(panel) : 0;
    const activeIndex = panel ? getActiveStepIndex(panel) : 0;

    if (count > 1 && activeIndex >= count - 1) {
      scrollEl.dataset.scrollFade = 'none';
      return;
    }

    const maxScroll = scrollEl.scrollWidth - scrollEl.clientWidth;
    const atStart = scrollEl.scrollLeft <= SCROLL_FADE_THRESHOLD;
    const atEnd = scrollEl.scrollLeft >= maxScroll - SCROLL_FADE_THRESHOLD;

    if (atStart && atEnd) {
      scrollEl.dataset.scrollFade = 'none';
    } else if (atEnd) {
      scrollEl.dataset.scrollFade = 'none';
    } else {
      scrollEl.dataset.scrollFade = 'end';
    }
  }

  /**
   * @param {HTMLElement} rail
   * @param {HTMLElement} dot
   * @returns {number}
   */
  function getDotCenterInRail(rail, dot) {
    const railRect = rail.getBoundingClientRect();
    const dotRect = dot.getBoundingClientRect();

    return dotRect.left + dotRect.width / 2 - railRect.left;
  }

  /**
   * Mobile rail shows two steps at a time; leftmost visible dot index for step 1.
   *
   * @param {number} safeIndex
   * @param {number} count
   * @returns {number}
   */
  function getMobileLeftmostDotIndex(safeIndex, count) {
    if (count <= 1) {
      return 0;
    }

    if (safeIndex <= 0) {
      return 0;
    }

    if (safeIndex >= count - 1) {
      return count - 2;
    }

    return safeIndex;
  }

  /**
   * Mobile step 2+: track start in rail-local px so passed fill bleeds to the
   * viewport left edge (page margin + scroll offset). Uses target scroll when
   * smooth scroll has not settled yet.
   *
   * @param {HTMLElement} rail
   * @param {HTMLElement} scrollEl
   * @param {HTMLElement} panel
   * @param {number} safeIndex
   * @param {number} count
   * @returns {number}
   */
  function getMobilePassedTrackStart(rail, scrollEl, panel, safeIndex, count) {
    const scrollRect = scrollEl.getBoundingClientRect();
    const railRect = rail.getBoundingClientRect();
    let scrollportInRail = scrollRect.left - railRect.left;
    const targetScroll = getRailScrollTarget(scrollEl, panel, safeIndex, count);

    if (scrollEl.scrollLeft + SCROLL_FADE_THRESHOLD < targetScroll) {
      scrollportInRail = Math.max(scrollportInRail, targetScroll);
    }

    const pageMargin = scrollRect.left;

    return -(scrollportInRail + pageMargin);
  }

  /**
   * @param {HTMLElement} panel
   * @param {number} safeIndex
   * @param {number} count
   */
  function syncRailProgress(panel, safeIndex, count) {
    const rail = panel.querySelector('.glowwa-growth-timeline__rail');
    const segments = panel.querySelectorAll(
      '.glowwa-growth-timeline__rail-segment',
    );
    const scrollEl = panel.querySelector('[data-glowwa-gt-rail-scroll]');

    if (!(rail instanceof HTMLElement) || segments.length === 0) {
      return;
    }

    const dotCenters = [...segments].map((segment) => {
      const dot = segment.querySelector('.glowwa-growth-timeline__rail-dot');
      return dot instanceof HTMLElement
        ? getDotCenterInRail(rail, dot)
        : 0;
    });

    const firstDotCenter = dotCenters[0];
    const lastDotCenter = dotCenters[count - 1];
    const isMobileLastStep =
      MOBILE_MQ.matches &&
      scrollEl instanceof HTMLElement &&
      count > 1 &&
      safeIndex >= count - 1;

    panel.dataset.glowwaGtRailAtEnd = isMobileLastStep ? 'true' : 'false';

    let trackEnd = Math.max(0, rail.offsetWidth - lastDotCenter);
    let fillEnd = lastDotCenter;
    let trackStart = firstDotCenter;

    if (count <= 1) {
      fillEnd = rail.offsetWidth;
      trackEnd = 0;
    } else if (safeIndex >= count - 1) {
      fillEnd = MOBILE_MQ.matches ? lastDotCenter : rail.offsetWidth;
    } else {
      fillEnd =
        (dotCenters[safeIndex] + dotCenters[safeIndex + 1]) / 2;
    }

    if (!MOBILE_MQ.matches) {
      trackEnd = 0;
    } else if (count > 1) {
      if (safeIndex > 0 && scrollEl instanceof HTMLElement) {
        trackStart = getMobilePassedTrackStart(
          rail,
          scrollEl,
          panel,
          safeIndex,
          count,
        );
      } else {
        const leftmostDotIndex = getMobileLeftmostDotIndex(safeIndex, count);
        trackStart = dotCenters[leftmostDotIndex] ?? firstDotCenter;
      }
    }

    const trackWidth = Math.max(0, rail.offsetWidth - trackStart - trackEnd);
    const fillWidth = Math.max(0, Math.min(fillEnd - trackStart, trackWidth));
    const progress = trackWidth > 0 ? fillWidth / trackWidth : 1;

    panel.style.setProperty('--glowwa-gt-rail-track-start', `${trackStart}px`);
    panel.style.setProperty('--glowwa-gt-rail-track-end', `${trackEnd}px`);
    panel.style.setProperty('--glowwa-gt-rail-progress', String(progress));
    panel.style.setProperty('--glowwa-gt-step-count', String(count));

    segments.forEach((segment, stepIndex) => {
      segment.classList.toggle(
        'is-passed',
        fillEnd + 0.5 >= dotCenters[stepIndex],
      );
    });
  }

  /**
   * @param {HTMLElement} panel
   */
  function syncMobileRailMetrics(panel) {
    const scrollEl = panel.querySelector('[data-glowwa-gt-rail-scroll]');
    if (!(scrollEl instanceof HTMLElement)) {
      return;
    }

    if (!MOBILE_MQ.matches) {
      scrollEl.style.removeProperty('--glowwa-gt-mobile-rail-step');
      updateRailScrollFade(scrollEl, panel);
      return;
    }

    const stepWidth = scrollEl.clientWidth / getMobileVisibleSteps(panel);
    scrollEl.style.setProperty(
      '--glowwa-gt-mobile-rail-step',
      `${stepWidth}px`,
    );
    updateRailScrollFade(scrollEl, panel);
  }

  /**
   * @param {HTMLElement} scrollEl
   * @param {HTMLElement} panel
   * @param {number} index
   * @param {number} count
   * @returns {number}
   */
  function getRailScrollTarget(scrollEl, panel, index, count) {
    const maxScroll = Math.max(0, scrollEl.scrollWidth - scrollEl.clientWidth);
    const edgeInset = 8;
    const scrollRect = scrollEl.getBoundingClientRect();

    if (index <= 0 || count <= 1) {
      return 0;
    }

    const scrollIndex =
      index >= count - 1 ? Math.max(0, count - 2) : index;

    if (scrollIndex <= 0) {
      return 0;
    }

    const segment = panel.querySelector(
      `.glowwa-growth-timeline__rail-segment[data-glowwa-gt-rail-step="${scrollIndex}"]`,
    );

    if (!(segment instanceof HTMLElement)) {
      return 0;
    }

    const segmentRect = segment.getBoundingClientRect();
    const targetLeft =
      scrollEl.scrollLeft +
      (segmentRect.left - scrollRect.left) -
      edgeInset;

    return Math.max(0, Math.min(targetLeft, maxScroll));
  }

  /**
   * @param {HTMLElement} panel
   * @param {number} index
   * @param {(() => void) | undefined} [onComplete]
   */
  function scrollRailToStep(panel, index, onComplete) {
    if (!MOBILE_MQ.matches) {
      onComplete?.();
      return;
    }

    const scrollEl = panel.querySelector('[data-glowwa-gt-rail-scroll]');
    if (!(scrollEl instanceof HTMLElement)) {
      onComplete?.();
      return;
    }

    const count = getStepCount(panel);
    const targetLeft = getRailScrollTarget(scrollEl, panel, index, count);

    const finishScroll = () => {
      requestAnimationFrame(() => {
        updateRailScrollFade(scrollEl, panel);
        syncRailProgress(panel, index, count);
        onComplete?.();
      });
    };

    if (
      prefersReducedMotion ||
      Math.abs(scrollEl.scrollLeft - targetLeft) <= SCROLL_FADE_THRESHOLD
    ) {
      scrollEl.scrollLeft = targetLeft;
      finishScroll();
      return;
    }

    scrollEl.scrollTo({
      left: targetLeft,
      behavior: 'smooth',
    });

    if (typeof scrollEl.scrollend !== 'undefined') {
      scrollEl.addEventListener('scrollend', finishScroll, { once: true });
    } else {
      window.setTimeout(finishScroll, 450);
    }
  }

  /**
   * Swaps the panel's hero media to the step's own image, when that step has
   * one. Steps without an image are a no-op — whatever image is currently
   * showing is left in place.
   *
   * @param {HTMLElement} panel
   * @param {number} index
   */
  function swapStepImage(panel, index) {
    const template = panel.querySelector(
      `[data-glowwa-gt-step-image="${index}"]`,
    );
    if (!(template instanceof HTMLTemplateElement)) {
      return;
    }

    const media = panel.querySelector('[data-glowwa-gt-media]');
    if (!(media instanceof HTMLElement)) {
      return;
    }

    media.replaceChildren(template.content.cloneNode(true));
  }

  /**
   * @param {HTMLElement} panel
   * @param {number} index
   * @param {object} [options]
   * @param {boolean} [options.swapImage] - Whether to swap the panel's hero
   *   media to this step's image. Liquid already renders the correct image
   *   for the panel's starting step, so initial/setup calls pass `false`
   *   here to avoid an unnecessary (and visually flashy) DOM replacement on
   *   load. Only actual user-driven navigation should swap the image.
   */
  function setActiveStep(panel, index, { swapImage = true } = {}) {
    const steps = panel.querySelectorAll('[data-glowwa-gt-step]');
    const count = steps.length;
    const safeIndex = Math.min(Math.max(index, 0), Math.max(count - 1, 0));
    const stepsRoot = panel.querySelector('.glowwa-growth-timeline__steps');

    if (swapImage) {
      swapStepImage(panel, safeIndex);
    }

    steps.forEach((step, i) => {
      const isActive = i === safeIndex;
      step.classList.toggle('is-active', isActive);
      step.setAttribute('aria-hidden', isActive ? 'false' : 'true');
    });

    panel.querySelectorAll('[data-glowwa-gt-rail-step]').forEach((control) => {
      const stepIndex = Number(control.dataset.glowwaGtRailStep || '0');
      const isActive = stepIndex === safeIndex;
      const isComplete =
        control.classList.contains('glowwa-growth-timeline__rail-segment') &&
        stepIndex < safeIndex;

      control.classList.toggle('is-active', isActive);
      control.classList.toggle('is-complete', isComplete);
      control.classList.remove('is-partial');

      if (control.classList.contains('glowwa-growth-timeline__rail-segment')) {
        control.setAttribute('aria-current', isActive ? 'step' : 'false');
      }
    });

    panel.dataset.glowwaGtActiveStep = String(safeIndex);

    const prev = panel.querySelector('[data-glowwa-gt-prev]');
    const next = panel.querySelector('[data-glowwa-gt-next]');
    if (prev) {
      prev.disabled = safeIndex <= 0;
    }
    if (next) {
      next.disabled = safeIndex >= count - 1;
    }

    if (stepsRoot instanceof HTMLElement) {
      syncStepsHeight(stepsRoot);
    }

    syncMobileRailMetrics(panel);
    syncRailProgress(panel, safeIndex, count);

    if (MOBILE_MQ.matches) {
      scrollRailToStep(panel, safeIndex);
    }
  }

  /**
   * @param {HTMLElement} root
   * @param {string} productId
   */
  function activateProduct(root, productId) {
    const pills = root.querySelectorAll('.glowwa-growth-timeline__pill');
    const panels = root.querySelectorAll('[data-glowwa-gt-panel]');

    pills.forEach((pill) => {
      const isActive = pill.dataset.glowwaGtProductId === productId;
      pill.classList.toggle('is-active', isActive);
      pill.setAttribute('aria-selected', isActive ? 'true' : 'false');
      pill.tabIndex = isActive ? 0 : -1;
    });

    panels.forEach((panel) => {
      const isActive = panel.dataset.glowwaGtProductId === productId;
      panel.classList.toggle('is-active', isActive);
      panel.hidden = !isActive;
      if (isActive) {
        // Unlike the one-time page-load call, this panel may have been
        // navigated before (its media may still hold a previously swapped
        // step image), so the image must reset to step 0's on every switch.
        setActiveStep(panel, 0);
      }
    });
  }

  /**
   * @param {HTMLElement} panel
   * @param {number} delta
   */
  function changeStep(panel, delta) {
    if (panel.dataset.glowwaGtTransitioning === 'true') {
      return;
    }

    const nextIndex = getActiveStepIndex(panel) + delta;
    const count = getStepCount(panel);

    if (nextIndex < 0 || nextIndex >= count) {
      return;
    }

    if (prefersReducedMotion) {
      setActiveStep(panel, nextIndex);
      return;
    }

    panel.dataset.glowwaGtTransitioning = 'true';
    setActiveStep(panel, nextIndex);

    window.setTimeout(() => {
      delete panel.dataset.glowwaGtTransitioning;
    }, STEP_TRANSITION_MS);
  }

  /**
   * @param {HTMLElement} root
   */
  function wireRoot(root) {
    if (root.dataset.glowwaGtInit === 'true') {
      return;
    }
    root.dataset.glowwaGtInit = 'true';

    root.querySelectorAll('.glowwa-growth-timeline__pill').forEach((pill) => {
      pill.addEventListener('click', () => {
        const productId = pill.dataset.glowwaGtProductId;
        if (!productId) {
          return;
        }
        activateProduct(root, productId);
      });

      pill.addEventListener('keydown', (event) => {
        if (event.key !== 'ArrowRight' && event.key !== 'ArrowLeft') {
          return;
        }
        const pills = [...root.querySelectorAll('.glowwa-growth-timeline__pill')];
        const index = pills.indexOf(pill);
        if (index < 0) {
          return;
        }
        event.preventDefault();
        const delta = event.key === 'ArrowRight' ? 1 : -1;
        const next = pills[index + delta];
        if (next) {
          next.focus();
          next.click();
        }
      });
    });

    root.querySelectorAll('[data-glowwa-gt-panel]').forEach((panel) => {
      const stepsRoot = panel.querySelector('.glowwa-growth-timeline__steps');
      if (stepsRoot instanceof HTMLElement) {
        syncStepsHeight(stepsRoot);
        new ResizeObserver(() => syncStepsHeight(stepsRoot)).observe(stepsRoot);
      }

      panel.querySelectorAll('[data-glowwa-gt-rail-step]').forEach((control) => {
        control.addEventListener('click', () => {
          const index = Number(control.dataset.glowwaGtRailStep || '0');
          if (panel.dataset.glowwaGtTransitioning === 'true') {
            return;
          }
          if (prefersReducedMotion) {
            setActiveStep(panel, index);
            return;
          }
          panel.dataset.glowwaGtTransitioning = 'true';
          setActiveStep(panel, index);
          window.setTimeout(() => {
            delete panel.dataset.glowwaGtTransitioning;
          }, STEP_TRANSITION_MS);
        });
      });

      const prev = panel.querySelector('[data-glowwa-gt-prev]');
      const next = panel.querySelector('[data-glowwa-gt-next]');

      if (prev) {
        prev.addEventListener('click', () => {
          changeStep(panel, -1);
        });
      }

      if (next) {
        next.addEventListener('click', () => {
          changeStep(panel, 1);
        });
      }

      if (panel.classList.contains('is-active')) {
        setActiveStep(panel, 0, { swapImage: false });
      }

      const railScroll = panel.querySelector('[data-glowwa-gt-rail-scroll]');
      if (railScroll instanceof HTMLElement) {
        syncMobileRailMetrics(panel);

        railScroll.addEventListener(
          'scroll',
          () => {
            updateRailScrollFade(railScroll, panel);
          },
          { passive: true },
        );

        window.addEventListener(
          'resize',
          () => {
            syncMobileRailMetrics(panel);
            const activeIndex = getActiveStepIndex(panel);
            const count = getStepCount(panel);
            syncRailProgress(panel, activeIndex, count);
            scrollRailToStep(panel, activeIndex, () => {
              syncRailProgress(panel, activeIndex, count);
            });
          },
          { passive: true },
        );
      }
    });
  }

  function initAll() {
    document.querySelectorAll(ROOT).forEach(wireRoot);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll);
  } else {
    initAll();
  }

  document.addEventListener('shopify:section:load', (event) => {
    const root = event.target?.querySelector?.(ROOT);
    if (root instanceof HTMLElement) {
      wireRoot(root);
    }
  });
})();
